/**
 * Table layout state (column visibility and order, grouping, sorting) is
 * persisted in session storage under the config's `tableUniqueKey`, so it can
 * outlive the config it was written against: a later mount may reuse the key
 * with different columns, or the config may change under a mounted table.
 * Defaults and the stored layout are both resolved against the current config
 * during render, and a stored layout failing that config's schema is replaced
 * by the defaults in the same render.
 */
import {Dispatch, SetStateAction, useCallback, useMemo} from "react";
import {typedEntries} from "../../utils/typescript";
import {useSessionState} from "../sessionState";
import {isEqual} from "lodash-es";
import {Value} from "typebox/value";
import useComputedState from "../computedState";
import {ColumnId, TableConfig} from "./config";
import {
  generatePersistedTableStateSchema,
  GroupState,
  PersistedStateSchema,
  PersistedTableState,
  SetColumnPositionFunction,
  SetColumnVisibleFunction,
  SetGroupStateFunction,
  SetSortStateFunction,
  SortState,
  TableState,
} from "./state";
import {StaticDecode} from "typebox";
import {getRenderableColumnIds, isColumnSortable} from "./utility";

/** Column sort direction. */

export type UseTableStateOpts<C extends TableConfig> = {
  /**
   * Which columns are hidden by default? If omitted, all columns are visible by
   * default.
   */
  defaultHiddenColumns?: readonly ColumnId<C>[];

  /**
   * Provide a default group state. If omitted, no grouping is applied by
   * default.
   */
  defaultGroupState?: GroupState<C>;

  /**
   * Provide a default sort state. If omitted, the first sortable column in
   * ascending order is used.
   */
  defaultSortState?: SortState<C>;
};

function resolveDefaultColumnVisibility<C extends TableConfig>(
  config: C,
  opts?: UseTableStateOpts<C>,
): Record<ColumnId<C>, boolean> {
  const columnIds = getRenderableColumnIds(config);
  const defaultHiddenColumns = opts?.defaultHiddenColumns ?? [];

  return columnIds.reduce(
    (acc, column) => {
      acc[column] = !defaultHiddenColumns.includes(column);
      return acc;
    },
    {} as Record<ColumnId<C>, boolean>,
  );
}

function resolveDefaultColumnOrder<C extends TableConfig>(config: C): ColumnId<C>[] {
  return getRenderableColumnIds(config);
}

function resolveDefaultSortState<C extends TableConfig>(config: C, opts?: UseTableStateOpts<C>): SortState<C> {
  let sortState = opts?.defaultSortState;

  // `null` is a valid value for the sort state, so we explicitly check for
  // `undefined` only.
  if (sortState === undefined) {
    const firstSortableColumn = typedEntries(config.columns).find(([_, column]) => isColumnSortable(column))?.[0];
    sortState = firstSortableColumn != null ? {columnId: firstSortableColumn, direction: "ascending"} : null;
  }

  return sortState;
}

function resolveDefaultGroupState<C extends TableConfig>(_config: C, opts?: UseTableStateOpts<C>): GroupState<C> {
  return opts?.defaultGroupState ?? [];
}

export function useDefaultPersistedTableState<C extends TableConfig>(
  config: C,
  opts?: UseTableStateOpts<C>,
): PersistedTableState<C> {
  // `opts` is often an inline literal, so it is stabilised by value first.
  const [stableOpts] = useComputedState<UseTableStateOpts<C> | undefined>(prev => (isEqual(prev, opts) ? prev : opts));
  return useMemo<PersistedTableState<C>>(
    () => ({
      columnVisibility: resolveDefaultColumnVisibility(config, stableOpts),
      columnOrder: resolveDefaultColumnOrder(config),
      groupState: resolveDefaultGroupState(config, stableOpts),
      sortState: resolveDefaultSortState(config, stableOpts),
    }),
    [config, stableOpts],
  );
}
export function usePersistedTableState<C extends TableConfig>(
  config: C,
  defaultPersistedState: PersistedTableState<C>,
): [PersistedTableState<C>, Dispatch<SetStateAction<PersistedTableState<C>>>] {
  const schema = useMemo(() => generatePersistedTableStateSchema(config), [config]);
  const [storedState, setState] = useSessionState(
    `tableState.${config.tableUniqueKey}`,
    schema,
    defaultPersistedState as unknown as StaticDecode<PersistedStateSchema<C>>,
  ) as unknown as [PersistedTableState<C>, Dispatch<SetStateAction<PersistedTableState<C>>>];
  // `useSessionState` re-reads storage in an effect, one render after the schema changes.
  const state = useMemo(
    () => (Value.Check(schema, storedState) ? storedState : defaultPersistedState),
    [schema, storedState, defaultPersistedState],
  );
  return [state, setState];
}

export function useProjectedTableState<C extends TableConfig>(
  config: C,
  state: PersistedTableState<C>,
  setState: Dispatch<SetStateAction<PersistedTableState<C>>>,
  defaultPersistedState: PersistedTableState<C>,
): TableState<C> {
  const {columnVisibility, groupState, sortState, columnOrder} = state;

  // Transform the column visibility record into a `Set` of visible columns
  // which is easier to consume.
  const visibleColumns = useMemo(
    () =>
      new Set(
        typedEntries(columnVisibility)
          .filter(([_columnId, visible]) => visible)
          .map(([columnId]) => columnId),
      ),
    [columnVisibility],
  );

  const setVisibleColumns = useCallback(
    (action: SetStateAction<Set<ColumnId<C>>>) => {
      setState(state => {
        const next = new Set(visibleColumns);
        const updated = action instanceof Function ? action(next) : action;

        const columnVisibility = getRenderableColumnIds(config).reduce(
          (acc, columnId) => {
            acc = {...acc, [columnId]: updated.has(columnId)};
            return acc;
          },
          {} as Record<ColumnId<C>, boolean>,
        );

        return {...state, columnVisibility};
      });
    },
    [config, setState, visibleColumns],
  );

  const setGroupState = useCallback<SetGroupStateFunction<C>>(
    action => {
      setState(state => {
        return {...state, groupState: action instanceof Function ? action(state.groupState) : action};
      });
    },
    [setState],
  );

  const setSortState = useCallback<SetSortStateFunction<C>>(
    action => {
      setState(state => {
        return {...state, sortState: action instanceof Function ? action(state.sortState) : action};
      });
    },
    [setState],
  );

  const setColumnVisible = useCallback<SetColumnVisibleFunction<C>>(
    (columnId, action) => {
      const allowed = !config.columns[columnId as string].forceVisible;

      if (!allowed) {
        return;
      }

      setVisibleColumns(prev => {
        const next = new Set(prev);
        const visible = typeof action === "function" ? action(prev.has(columnId)) : action;

        if (visible) {
          next.add(columnId);
        } else {
          next.delete(columnId);
        }
        return next;
      });
    },
    [config.columns, setVisibleColumns],
  );

  const setColumnPosition = useCallback<SetColumnPositionFunction<C>>(
    (columnId, action) => {
      setState(prev => {
        const next = [...prev.columnOrder];
        const index = next.indexOf(columnId);

        const updated = action instanceof Function ? action(index) : action;

        if (updated === index) {
          return prev;
        }

        next.splice(index, 1);
        next.splice(updated, 0, columnId);

        return {...prev, columnOrder: next};
      });
    },
    [setState],
  );

  const reset = useCallback(() => {
    setState(defaultPersistedState);
  }, [defaultPersistedState, setState]);

  const isChangedFromDefault = useMemo(() => {
    return !isEqual(state, defaultPersistedState);
  }, [defaultPersistedState, state]);

  const tableState = useMemo(
    () => ({
      visibleColumns,
      groupState,
      sortState,
      columnOrder,

      isChangedFromDefault,

      setColumnVisible,
      setGroupState,
      setSortState,
      setColumnPosition,

      reset,
    }),
    [
      columnOrder,
      groupState,
      isChangedFromDefault,
      reset,
      setColumnPosition,
      setColumnVisible,
      setGroupState,
      setSortState,
      sortState,
      visibleColumns,
    ],
  );

  return tableState;
}

export function useTableState<C extends TableConfig>(config: C, opts?: UseTableStateOpts<C>): TableState<C> {
  const defaultPersistedState = useDefaultPersistedTableState(config, opts);
  const [state, setState] = usePersistedTableState(config, defaultPersistedState);
  return useProjectedTableState(config, state, setState, defaultPersistedState);
}
